/**
 * YouTube link parsing for the campaign media gallery.
 *
 * Mirrors app/Services/Campaign/YouTubeUrl.php — the client validates the
 * pasted link before posting, so any shape the front-end rejects can never
 * reach the server no matter how tolerant the server is. Keep the two in sync.
 */

/** A YouTube video id: exactly 11 chars of the URL-safe base64 alphabet. */
const ID = '[A-Za-z0-9_-]{11}'

const PATTERNS = [
  // youtu.be/ID
  new RegExp(`youtu\\.be/(${ID})(?![A-Za-z0-9_-])`, 'i'),
  // youtube.com/embed/ID, /v/ID, /e/ID, /shorts/ID, /live/ID
  new RegExp(`youtube\\.com/(?:embed|v|e|shorts|live)/(${ID})(?![A-Za-z0-9_-])`, 'i'),
  // youtube.com/watch?v=ID — v may sit anywhere in the query string
  new RegExp(`youtube\\.com/.*[?&]v=(${ID})(?![A-Za-z0-9_-])`, 'i'),
]

const BARE_ID = new RegExp(`^${ID}$`)

/**
 * Extracts the video id from any accepted YouTube link shape, else null.
 *
 * /shorts/ and /live/ are included because that is what a phone share sheet
 * produces; the previous pattern rejected both with "Invalid YouTube URL",
 * so an owner sharing from their phone could never add their video.
 *
 * `allowBareId` marks the difference between an input boundary and a stored
 * field. At a boundary — Edit Campaign, and YouTubeUrl::id() server-side —
 * an owner may paste the naked id straight out of YouTube's share panel, so
 * accepting it is the whole point, and the default stays true to keep the two
 * implementations in step. Pass false when re-reading a value the system
 * already stored: those are URL-shaped by construction, and accepting a naked
 * id there means any 11-character slug someone typed into a url field is read
 * as a video. `founder_cta.video_url` is free text an admin fills in, so
 * "founder-msg" is exactly 11 legal characters and would embed as a video id.
 */
export function extractYoutubeID(url, { allowBareId = true } = {}) {
  const value = String(url || '').trim()

  if (!value) return null
  if (allowBareId && BARE_ID.test(value)) return value

  for (const pattern of PATTERNS) {
    const match = value.match(pattern)
    if (match) return match[1]
  }

  return null
}

/** Player URL stored on the media item and embedded in the lightbox. */
export function youtubeEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}`
}

/** Poster served straight off YouTube's CDN; exists for every live video. */
export function youtubeThumbnailUrl(videoId) {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}
